import * as Blockly from 'blockly-tradais';
import { javascriptGenerator } from 'blockly-tradais/javascript';
import { mapAllowedTypeToCheck } from './value-function-types';

/**
 * `return_value` — the only way an input Value Function answers.
 *
 * Structurally identical to `return_boolean` (conditional-function-helper.ts) except its VALUE
 * socket is not fixed to Boolean: the check comes from the BINDING the workspace was opened for,
 * applied by applyReturnValueCheck(). One block type, one generator, so the estate does not grow a
 * return block per scalar type — the pattern Block Reduction 2026-06 retired.
 */
export const ValueFns = {
  customBlockForReturnValue() {
    Blockly.Blocks['return_value'] = {
      init(this: any) {
        this.appendValueInput('VALUE').appendField('return');
        this.setPreviousStatement(true, null);
        this.setColour('#5C81A6');
        this.setTooltip(
          'Returns the value for the bound component input. Must be the last block.'
        );
        this.setHelpUrl('');

        // PERMISSIVE ON INIT, ALWAYS. init() runs before the binding context is known and before
        // deserialization restores children, so a real check here would REFUSE saved value blocks
        // and drop them on reload — the exact trap documented on set_aob_variable and get_variable
        // in core-variable-blocks.ts. The real check arrives via applyReturnValueCheck().
        this.getInput('VALUE').setCheck(null);

        const block: any = this;
        this.setOnChange(function (e: any) {
          if (!block.workspace || block.isInFlyout || !e) return;
          // Re-apply AFTER load, never during. The allowedType is parked on the workspace by
          // applyReturnValueCheck so a block restored later still lands on the right check.
          if (e.type === (Blockly as any).Events.FINISHED_LOADING) {
            applyReturnValueCheck(block.workspace, (block.workspace as any).__valueAllowedType);
          }
        });
      },
    };

    (javascriptGenerator as any).forBlock['return_value'] = function (block: any) {
      const value =
        (javascriptGenerator as any).valueToCode(
          block,
          'VALUE',
          (javascriptGenerator as any).ORDER_NONE
        ) || 'null';
      return `return ${value};\n`;
    };
  },
};

/**
 * Point every `return_value` in this workspace at the binding's allowed types.
 *
 * Called by the host when the Blockly editor opens for a specific input binding, and again from the
 * block's own FINISHED_LOADING handler. `undefined` allowedType restores the permissive socket,
 * which is the correct state when no binding context exists (e.g. the palette flyout).
 */
export function applyReturnValueCheck(workspace: any, allowedType: string | undefined): void {
  if (!workspace) return;
  // Parked on the workspace so the FINISHED_LOADING re-apply has the same answer as the open call.
  (workspace as any).__valueAllowedType = allowedType;
  const check = allowedType ? mapAllowedTypeToCheck(allowedType) : null;
  for (const b of workspace.getAllBlocks(false)) {
    if (b.type !== 'return_value') continue;
    const input = b.getInput('VALUE');
    if (input) input.setCheck(check);
  }
}
